import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'

const services = [
  { name: 'Roofing', path: '/services/roofing' },
  { name: 'Flat Roofing', path: '/services/flat-roofing' },
  { name: 'General Building', path: '/services/general-building' },
  { name: 'Guttering & Fascias', path: '/services/guttering-fascias' },
  { name: 'Leadwork', path: '/services/leadwork' },
  { name: 'Re-pointing', path: '/services/repointing' },
  { name: 'Lime Pointing', path: '/services/lime-pointing' },
  { name: 'Render Repairs', path: '/services/render-repairs' },
  { name: 'Damp Proofing', path: '/services/damp-proofing' },
  { name: 'Loft Insulation', path: '/services/loft-insulation' },
  { name: 'Plastering', path: '/services/plastering' },
  { name: 'Decorating', path: '/services/decorating' },
  { name: 'Sash Window Repairs', path: '/services/sash-window-repairs' },
  { name: 'Drone Surveys', path: '/services/drone-surveys' },
  { name: 'Chimney Demolition', path: '/services/chimney-demolition' },
]

function Navigation() {
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  
  // Check if current path is a service page
  const isServicePage = location.pathname.startsWith('/services')

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsServicesOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'Contact Us', path: '/contact' },
  ]

  return (
    <nav className="bg-white border-t-2 border-gray-200 shadow-sm relative">
      <div className="container mx-auto px-4">
        {/* Desktop Navigation */}
        <div className="hidden lg:block py-2">
          <ul className="flex items-center justify-center gap-2">
            {navItems.map((item) => (
              <li key={item.path} className="relative group">
                {item.hasDropdown ? (
                  <>
                    <Link
                      to={item.path}
                      className={cn(
                        "inline-flex items-center gap-1 px-6 py-3 text-base font-medium rounded-md hover:bg-gray-100 transition-colors",
                        isServicePage && "bg-primary text-white hover:bg-primary/90"
                      )}
                    >
                      {item.name}
                      <ChevronDown size={16} />
                    </Link>
                    
                    {/* Desktop Dropdown */}
                    <div className="absolute top-full left-0 mt-1 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 z-50">
                      <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 w-[700px] -left-[300px] relative">
                        <Link
                          to="/services"
                          className="block p-4 mb-4 rounded-md hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                        >
                          <div className="text-base font-semibold text-gray-900">View All Services</div>
                          <p className="text-sm text-gray-600 mt-1">Browse our complete list of services</p>
                        </Link>
                        
                        <div className="grid grid-cols-3 gap-2">
                          {services.map((service) => (
                            <Link
                              key={service.path}
                              to={service.path}
                              className={cn(
                                "block p-3 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all",
                                location.pathname === service.path && "bg-primary/10 text-primary"
                              )}
                            >
                              {service.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={cn(
                      "inline-flex items-center px-6 py-3 text-base font-medium rounded-md hover:bg-gray-100 transition-colors",
                      location.pathname === item.path && "bg-primary text-white hover:bg-primary/90"
                    )}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between py-3">
            <span className="text-gray-600 text-sm">Menu</span>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl z-50 overflow-y-auto lg:hidden"
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Menu</h2>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                    aria-label="Close menu"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="p-4">
                <ul className="space-y-2">
                  {navItems.map((item) => (
                    <li key={item.path}>
                      {item.hasDropdown ? (
                        <div>
                          <button
                            onClick={() => setIsServicesOpen(!isServicesOpen)}
                            className={cn(
                              "w-full flex items-center justify-between p-3 rounded-md text-left font-medium transition-colors",
                              isServicePage ? "bg-primary text-white" : "hover:bg-gray-100"
                            )}
                          >
                            {item.name}
                            <ChevronDown
                              size={20}
                              className={cn(
                                "transition-transform duration-200",
                                isServicesOpen && "rotate-180"
                              )}
                            />
                          </button>
                          
                          <AnimatePresence>
                            {isServicesOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="pl-4 pr-2 py-2 space-y-1">
                                  <Link
                                    to="/services"
                                    className="block p-3 rounded-md text-sm font-medium bg-gray-50 hover:bg-gray-100 transition-colors"
                                  >
                                    View All Services
                                  </Link>
                                  {services.map((service) => (
                                    <Link
                                      key={service.path}
                                      to={service.path}
                                      className={cn(
                                        "block p-3 rounded-md text-sm transition-colors",
                                        location.pathname === service.path
                                          ? "bg-primary/10 text-primary font-medium"
                                          : "hover:bg-gray-50"
                                      )}
                                    >
                                      {service.name}
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          to={item.path}
                          className={cn(
                            "block p-3 rounded-md font-medium transition-colors",
                            location.pathname === item.path
                              ? "bg-primary text-white"
                              : "hover:bg-gray-100"
                          )}
                        >
                          {item.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Mobile CTA */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <a
                    href="mailto:info@hill-bros.co.uk"
                    className="block w-full bg-primary hover:bg-primary/90 text-white text-center px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Get Free Quote
                  </a>
                  <div className="mt-4 space-y-2 text-sm text-gray-600">
                    <a href="tel:01273326200" className="flex items-center gap-2 hover:text-primary">
                      <Phone size={16} /> 01273 326200
                    </a>
                    <a href="mailto:info@hill-bros.co.uk" className="flex items-center gap-2 hover:text-primary">
                      <Mail size={16} /> info@hill-bros.co.uk
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navigation